// Credit Note Register (R2/R4/R5/R6/R8).
//
// The register is Debbie's paper book: every credit note raised, who raised it,
// which invoice it applies to, whether it has been emailed to the client, and
// whether it has been sitting unsent for more than a week. That last column is
// the reason the screen exists — the backend has computed the flag since the
// module shipped for INVOICES only, and for credit notes there was no flag, no
// filter, and no page to show either on.
//
// It is deliberately a separate screen from CreditNotesPage rather than more
// columns on it: that page is "find me a note" (search, type chips, paging) and
// this one is "what still needs chasing" (date range, vessel, alerts-only, and
// the tick that clears an alert). Same records, different question.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import ErrorModal from '../components/ErrorModal';
import LoadingState from '../components/LoadingState';
import { Modal } from '../components/Modal';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { usePagedRows } from '../hooks/usePagedRows';
import StatusBadge from '../components/StatusBadge';
import { EmptyState, ErrorState } from '../components/ui/States';
import { useToast } from '../context/ToastContext';
import { usePermission } from '../hooks/usePermission';
import {
  CreditNoteRegisterFilters,
  CreditNoteRegisterRow,
  downloadCreditNoteRegisterCsv,
  getCreditNoteRegister,
  markCreditNoteEmailed,
} from '../lib/creditNoteApi';
import { formatDate as fmtDate } from '../utils/dateFormat';
import { formatCurrency } from '../utils/priceUtils';
import DateInput from "../components/DateInput";

const INFO = (
  <div className="flex flex-col gap-3">
    <p>
      Every credit note raised, in the columns the paper register carried: clerk, PO number,
      owner, agent, which invoice it applies to, and whether it has gone to the client.
    </p>
    <p className="font-medium">The alert</p>
    <p>
      A note flags red once it is more than 7 days old and still has no Emailed mark. Marking it
      emailed is the only thing that clears the flag — so the mark is a record of what was sent,
      not a tidy-up button.
    </p>
  </div>
);

const STATUSES = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'applied', label: 'Applied' },
  { value: 'cancelled', label: 'Cancelled' },
];


function typeLabel(type: string) {
  if (type === 'item_return') return 'Item Return';
  if (type === 'discount') return 'Discount';
  if (type === 'correction') return 'Correction';
  return String(type || '--');
}

/** The recipient prompt. A real field rather than window.prompt(), because who the
 *  note went to is a register column somebody reads back later. */
const MarkEmailedModal: React.FC<{
  row: CreditNoteRegisterRow;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (emailedTo: string) => void;
}> = ({ row, saving, onCancel, onConfirm }) => {
  const [to, setTo] = useState(row.emailedTo || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Modal focuses its own panel a tick after it opens, which would swallow the
  // field's autoFocus. This is a data-entry dialog — the recipient is typed
  // immediately — so the field takes focus back once that has happened.
  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <Modal isOpen title={`Mark ${row.creditNoteNumber} as emailed`} onClose={onCancel} maxWidthClass="max-w-md">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          This records that the note went out and clears its one-week alert.
        </p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="credit-note-emailed-to">
            Sent to
          </label>
          <input
            id="credit-note-emailed-to"
            ref={inputRef}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !saving) onConfirm(to);
            }}
            placeholder="ops@vessel.com"
            className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm"
          />
          <p className="mt-1 text-xs text-slate-400">Optional — leave blank to just tick the column.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onConfirm(to)} loading={saving}>
            Mark emailed
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const CreditNoteRegisterPage: React.FC = () => {
  const toast = useToast();
  const { canEdit, canExport } = usePermission('invoices');

  const [rows, setRows] = useState<CreditNoteRegisterRow[]>([]);
  const pagedRows = usePagedRows(rows);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState<CreditNoteRegisterRow | null>(null);
  // `error` is for a failed ACTION (mark emailed, undo, export) — a modal,
  // because the user just pressed something and needs telling it did not happen.
  // A failed LOAD is the table's own state and stays on the page with a retry:
  // once its modal was dismissed the grid read "No credit notes match these
  // filters", which is a different and much worse thing to tell a chaser.
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [vessel, setVessel] = useState('');
  const [status, setStatus] = useState('all');
  const [alertOnly, setAlertOnly] = useState(false);

  const filters: CreditNoteRegisterFilters = useMemo(
    () => ({ from, to, vessel, status, alertOnly }),
    [from, to, vessel, status, alertOnly]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getCreditNoteRegister(filters));
      setLoadError(null);
    } catch {
      setRows([]);
      setLoadError('The credit note register could not be loaded. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    // Vessel is a free-text contains-match, so it is debounced with the rest of
    // the filters rather than firing a query per keystroke.
    const t = window.setTimeout(load, 250);
    return () => window.clearTimeout(t);
  }, [load]);

  const alertCount = rows.filter((r) => r.alert).length;

  const confirmMarkEmailed = async (emailedTo: string) => {
    if (!marking) return;
    setSaving(true);
    try {
      await markCreditNoteEmailed(marking.id, { emailedTo });
      toast.success(`${marking.creditNoteNumber} marked as emailed`);
      setMarking(null);
      await load();
    } catch (e: any) {
      setError({ title: 'Could not mark as emailed', message: e?.message || 'The credit note was not updated.' });
    } finally {
      setSaving(false);
    }
  };

  const undoEmailed = async (row: CreditNoteRegisterRow) => {
    try {
      await markCreditNoteEmailed(row.id, { emailed: false });
      toast.success(`Emailed mark removed from ${row.creditNoteNumber}`);
      await load();
    } catch (e: any) {
      setError({ title: 'Could not clear the mark', message: e?.message || 'The credit note was not updated.' });
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadCreditNoteRegisterCsv(filters);
    } catch (e: any) {
      setError({ title: 'Export failed', message: e?.message || 'The register could not be exported.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/credit-notes"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              aria-label="Back to credit notes"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            </Link>
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Credit note register</h1>
            <ModuleInfoButton content={INFO} ariaLabel="Credit note register information" />
            {alertCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                <AlertCircle aria-hidden="true" className="h-4 w-4" />
                {alertCount} unsent over a week
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canExport && (
              <Button onClick={doExport} loading={exporting}>
                Export CSV
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Every credit note raised, with its clerk, the invoice it applies to, and whether it has
          been emailed. Notes unsent for more than 7 days are flagged.
        </p>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 px-4 py-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-600">From</span>
              <DateInput validity="filter"
                value={from}
                onChange={(v) => setFrom(v)}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-600">To</span>
              <DateInput validity="filter"
                value={to}
                onChange={(v) => setTo(v)}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-600">Vessel</span>
              <input
                value={vessel}
                onChange={(e) => setVessel(e.target.value)}
                placeholder="Any vessel"
                className="h-9 w-48 rounded-md border border-slate-200 px-2 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-600">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            {/* Not a SegmentedControl: this is one independent on/off filter, not a
                pick-one-of-several, and it stays a pressed toggle. */}
            <Button
              onClick={() => setAlertOnly((v) => !v)}
              aria-pressed={alertOnly}
              className={alertOnly ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' : ''}
            >
              Alerts only
            </Button>
            {(from || to || vessel || status !== 'all' || alertOnly) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setFrom('');
                  setTo('');
                  setVessel('');
                  setStatus('all');
                  setAlertOnly(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {loadError ? (
            <div className="p-4">
              <ErrorState message={loadError} onRetry={load} />
            </div>
          ) : (
            <div className="overflow-auto">
              {/* `data-table` (index.css) supplies the header, cell padding and row
                  hover; `data-table-dense` because this is the bulk-scan register.
                  Only genuine per-cell overrides stay. */}
              <table className="data-table data-table-dense min-w-[1500px]">
                <thead>
                  <tr>
                    <th>CR number</th>
                    <th>Type</th>
                    <th>Clerk</th>
                    <th>Date</th>
                    <th>Vessel</th>
                    <th>Owner</th>
                    <th>Agent</th>
                    <th>PO #</th>
                    <th>Applies to invoice</th>
                    <th className="text-right">Amount</th>
                    <th>Emailed</th>
                    <th>Alert</th>
                    <th>Remarks</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={14} className="py-8">
                        <LoadingState text="Loading register..." />
                      </td>
                    </tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={14} className="py-10">
                        <EmptyState
                          title={alertOnly ? 'Nothing is overdue' : 'No credit notes match these filters'}
                          detail={
                            alertOnly
                              ? 'No unsent note is older than 7 days.'
                              : 'Widen the date range, or clear the vessel and status filters.'
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {pagedRows.visible.map((row) => (

                    <tr key={row.id} className={row.alert ? 'bg-red-50/40' : ''}>
                      <td className="font-medium">
                        <Link to={`/credit-notes/${row.id}`} className="text-primary-500 hover:underline">
                          {row.creditNoteNumber}
                        </Link>
                      </td>
                      <td>{typeLabel(row.type)}</td>
                      <td>{row.clerkName || '--'}</td>
                      <td>{fmtDate(row.date, '--')}</td>
                      <td>{row.vessel || '--'}</td>
                      <td>{row.ownerName || '--'}</td>
                      <td>{row.agentName || '--'}</td>
                      <td>{row.clientPoNumber || '--'}</td>
                      <td>{row.linkedInvoiceNumbers || '--'}</td>
                      <td className="text-right font-semibold tabular-nums text-red-700">
                        {/* D96.17 — in the note's own currency, never a guessed US$. */}
                        {formatCurrency(Number(row.grandTotal || 0), row.currency || null, { ceil: false })}
                      </td>
                      <td>
                        {row.emailed ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                              {fmtDate(row.emailedAt, 'Sent')}
                            </span>
                            {row.emailedTo && <span className="text-xs text-slate-500">{row.emailedTo}</span>}
                            {/* Item 38 (9 Oct 2026 QA retest): a cancelled note's emailed mark is
                                history, not an action — the server refuses this the same way it
                                refuses the button below (CREDIT_NOTE_CANCELLED). */}
                            {canEdit && row.status !== 'cancelled' && (
                              <button
                                onClick={() => undoEmailed(row)}
                                className="w-fit text-xs text-slate-400 underline hover:text-slate-600"
                              >
                                Undo
                              </button>
                            )}
                          </div>
                        ) : canEdit && row.status !== 'cancelled' ? (
                          <Button
                            size="sm"
                            onClick={() => setMarking(row)}
                            icon={<Mail aria-hidden="true" className="h-4 w-4" />}
                          >
                            Mark emailed
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Not emailed</span>
                        )}
                      </td>
                      <td>
                        {row.alert ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
                            <AlertCircle aria-hidden="true" className="h-4 w-4" />
                            {row.daysOld === null ? 'Overdue' : `${Math.floor(row.daysOld)} days`}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">--</span>
                        )}
                      </td>
                      <td className="max-w-[220px] truncate text-slate-600" title={row.remarks || ''}>
                        {row.remarks || '--'}
                      </td>
                      <td>{row.status ? <StatusBadge status={row.status} /> : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {loading ? (
            <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-600">Loading...</div>
          ) : (
            <Pagination page={pagedRows.page} pageSize={pagedRows.pageSize} total={pagedRows.total} onPageChange={pagedRows.setPage} noun="credit notes" />
          )}
        </div>
      </div>

      {marking && (
        <MarkEmailedModal
          row={marking}
          saving={saving}
          onCancel={() => setMarking(null)}
          onConfirm={confirmMarkEmailed}
        />
      )}

      <ErrorModal
        isOpen={Boolean(error)}
        title={error?.title || 'Error'}
        message={error?.message || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default CreditNoteRegisterPage;
